import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN, headers } from '../../../Reusable_Components/Config/apiConfig';
import { ToastContext } from '../../../../context/ToastContext';
import { fmtDate } from '../../../../utils/fmt';
import ConfirmModal from '../../../Reusable_Components/ConfirmModal';
import ConvertActionButton from '../../../Reusable_Components/ConvertActionButton';
import useDetailFetch from '../../../../hooks/useDetailFetch';

const STAGE_COLORS = {
  'Prospecting': '#64748b', 'Qualification': '#3b82f6', 'Proposal': '#8b5cf6',
  'Negotiation': '#f59e0b', 'Closed-Won': '#22c55e', 'Closed-Lost': '#ef4444',
};
const STAGE_ICONS = {
  'Prospecting': '🔍', 'Qualification': '✔', 'Proposal': '📄',
  'Negotiation': '🤝', 'Closed-Won': '🏆', 'Closed-Lost': '✗',
};

const probColor = (v) => {
  const n = Number(v);
  if (n >= 75) return '#22c55e';
  if (n >= 50) return '#3b82f6';
  if (n >= 25) return '#f59e0b';
  return '#ef4444';
};


const fmtKES = (n) => {
  const num = Number(n);
  if (!num || isNaN(num)) return '—';
  return 'KES ' + num.toLocaleString('en-KE');
};

const StageBadge = ({ value }) => {
  const color = STAGE_COLORS[value] || '#94a3b8';
  const icon = STAGE_ICONS[value] || '';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 14px', borderRadius: 99,
      background: color + '18', border: `1px solid ${color}40`,
      fontSize: 13, fontWeight: 600, color,
    }}>
      {icon} {value || '—'}
    </span>
  );
};

const Section = ({ icon, title, children }) => (
  <div className="card" style={{ marginBottom: 0 }}>
    <div className="card-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
        <div style={{ width: 32, height: 32, borderRadius: 7, background: 'var(--color-primary-pale)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{icon}</div>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-main)' }}>{title}</span>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, wide }) => (
  <div className={`form-field${wide ? ' span-full' : ''}`}>
    <div className="form-label">{label}</div>
    <div style={{ fontSize: 14, color: value ? 'var(--text-main)' : 'var(--text-muted)', fontWeight: value ? 500 : 400, padding: '8px 12px', background: 'var(--surface)', borderRadius: 6, border: '1px solid var(--border)', minHeight: 38, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

const SingleOpportunityDetails = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const id = opportunityId ? atob(opportunityId).replace(/\D/g, '') : '';
  const { data: opp, loading } = useDetailFetch(id ? `${API_DOMAIN}${API_ROUTES.opportunity}${id}/` : null);
  const [showDelete, setShowDelete] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [wonCustomer, setWonCustomer] = useState(null);
  const [originCustomer, setOriginCustomer] = useState(null);

  useEffect(() => {
    axios.get(`${API_DOMAIN}${API_ROUTES.quote}`, { headers, params: { page_size: 500 } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.data || []);
        setQuotes(list.filter(q => String(q.opportunity) === String(id)));
      })
      .catch(() => {});
  }, [id]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API_DOMAIN}${API_ROUTES.customer}`, { headers, params: { page_size: 500 } })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || res.data?.data || []);
        const won = list.find(c => String(c.source_opportunity) === String(id));
        setWonCustomer(won || null);
        if (opp?.existing_customer) {
          setOriginCustomer(list.find(c => String(c.id) === String(opp.existing_customer)) || null);
        }
      })
      .catch(() => {});
  }, [id, opp?.existing_customer]); // eslint-disable-line

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_DOMAIN}${API_ROUTES.opportunity}${id}/`, { headers });
      toast?.addToast({ type: 'success', message: 'Opportunity deleted.' });
      navigate('/opportunity/list');
    } catch {
      toast?.addToast({ type: 'error', message: 'Failed to delete opportunity.' });
    }
  };

  if (loading) return (
    <div className="card"><div className="card-body">
      {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 18, width: `${60 + i * 8}%`, borderRadius: 4, marginBottom: 12 }} />)}
    </div></div>
  );

  if (!opp) return (
    <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
      Opportunity not found. <Link to="/opportunity/list" style={{ color: 'var(--color-primary)' }}>Back to list</Link>
    </div></div>
  );

  const prob = Number(opp.probability_of_close) || 0;
  const value = Number(opp.value) || 0;
  const expectedRev = Math.round(value * prob / 100);
  const stageColor = STAGE_COLORS[opp.stage] || '#94a3b8';

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
            <Link to="/opportunity/list" style={{ color: 'var(--color-primary)' }}>Opportunities</Link>
            <span>›</span>
            <span>{opp.opportunity_name || `Opportunity #${opp.id}`}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>{opp.opportunity_name || `Opportunity #${opp.id}`}</h1>
            <StageBadge value={opp.stage} />
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 16, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)' }}>Value: <strong style={{ color: 'var(--text-main)' }}>{fmtKES(opp.value)}</strong></span>
            {prob > 0 && <span style={{ color: 'var(--text-muted)' }}>Win Prob: <strong style={{ color: probColor(prob) }}>{prob}%</strong></span>}
            {expectedRev > 0 && <span style={{ color: 'var(--text-muted)' }}>Expected: <strong style={{ color: 'var(--color-primary)' }}>{fmtKES(expectedRev)}</strong></span>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-danger" onClick={() => setShowDelete(true)}>Delete</button>
          <button className="btn btn-secondary" onClick={() => navigate(`/opportunity/input/${btoa(opp.id)}`)}>Edit Opportunity</button>
          <ConvertActionButton
            label="Generate Quote"
            url={`${API_DOMAIN}${API_ROUTES.opportunity}${opp.id}/generate-quote/`}
            body={{
              quote_number: `Q-${opp.id}-${Date.now()}`,
              expiration_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
            }}
            buildNavigateTo={(quote) => `/quote/input/${btoa(quote.id)}`}
            className="btn btn-secondary"
          />
          <ConvertActionButton
            label="Convert to Customer"
            url={`${API_DOMAIN}${API_ROUTES.opportunity}${opp.id}/convert-to-customer/`}
            buildNavigateTo={(customer) => `/customer/details/${btoa(customer.id)}`}
            disabled={opp.stage !== 'Closed-Won'}
            disabledTitle="Opportunity must be Closed-Won first"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>

        {/* Left */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <Section icon="💼" title="Deal Overview">
            <div className="form-grid">
              <Field label="Deal Type" value={opp.deal_type} />
              <Field label="Priority" value={opp.priority} />
              <Field label="Source" value={opp.source} />
              <Field label="Competitor" value={opp.competitor} />
            </div>
          </Section>

          <Section icon="🔗" title="Relationships">
            <div className="form-grid">
              <Field label="Primary Contact" value={opp.contact_name} />
              <Field label="Company / Account" value={opp.company} />
              <Field label="Related Lead ID" value={opp.related_lead ? `#${opp.related_lead}` : null} />
            </div>
          </Section>

          {(quotes.length > 0 || wonCustomer || originCustomer) && (
            <Section icon="🧭" title="Pipeline">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {originCustomer && (
                  <button className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => navigate(`/customer/details/${btoa(originCustomer.id)}`)}>
                    🏢 Expansion deal from: {originCustomer.name}
                  </button>
                )}
                {quotes.map(q => (
                  <button key={q.id} className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => navigate(`/single/quote/details/${btoa(q.id)}`)}>
                    📄 Quote {q.quote_number}
                  </button>
                ))}
                {wonCustomer && (
                  <button className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 8 }} onClick={() => navigate(`/customer/details/${btoa(wonCustomer.id)}`)}>
                    🏆 Became customer: {wonCustomer.name}
                  </button>
                )}
              </div>
            </Section>
          )}

          <Section icon="📅" title="Timeline">
            <div className="form-grid">
              <Field label="Expected Close Date" value={fmtDate(opp.expected_close_date)} />
              <Field label="Follow-up Date" value={fmtDate(opp.follow_up_date)} />
              <Field label="Created" value={fmtDate(opp.created_at)} />
              <Field label="Last Updated" value={fmtDate(opp.updated_at)} />
            </div>
          </Section>

          <Section icon="👥" title="Assignment &amp; Notes">
            <div className="form-grid">
              <Field label="Assigned To" value={Array.isArray(opp.assigned_to) ? opp.assigned_to.join(', ') : opp.assigned_to} wide />
              {opp.next_steps && <Field label="Next Steps" value={opp.next_steps} wide />}
              {opp.description && <Field label="Description" value={opp.description} wide />}
            </div>
          </Section>

          <Section icon="🔗" title="Related Items">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <Link to="/opportunity/activity/list" className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 6, fontSize: 13 }}>
                📋 Activities
              </Link>
              <Link to="/opportunity/note/list" className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 6, fontSize: 13 }}>
                📝 Notes
              </Link>
              <Link to="/opportunity/task/list" className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 6, fontSize: 13 }}>
                ✓ Tasks
              </Link>
              <Link to="/opportunity/meeting/list" className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 6, fontSize: 13 }}>
                🤝 Meetings
              </Link>
              <Link to="/opportunity/call/list" className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 6, fontSize: 13 }}>
                ☎️ Calls
              </Link>
              <Link to="/opportunity/event/list" className="btn btn-ghost" style={{ justifyContent: 'flex-start', gap: 6, fontSize: 13 }}>
                📅 Events
              </Link>
            </div>
          </Section>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Stage card */}
          <div className="card">
            <div className="card-header"><span className="card-title">Stage</span></div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: stageColor + '15', border: `1px solid ${stageColor}40` }}>
                <span style={{ fontSize: 20 }}>{STAGE_ICONS[opp.stage] || '📌'}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: stageColor }}>{opp.stage || '—'}</span>
              </div>
            </div>
          </div>

          {/* Financials */}
          <div className="card">
            <div className="card-header"><span className="card-title">Financials</span></div>
            <div className="card-body">
              {[
                { label: 'Deal Value',       value: fmtKES(opp.value) },
                { label: 'Win Probability',  value: `${prob}%` },
                { label: 'Expected Revenue', value: fmtKES(expectedRev), highlight: true },
              ].map(({ label, value: v, highlight }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderBottom: label !== 'Expected Revenue' ? '1px solid var(--border)' : 'none', fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ fontWeight: highlight ? 700 : 600, color: highlight ? 'var(--color-primary)' : 'var(--text-main)' }}>{v}</span>
                </div>
              ))}
              {/* Probability bar */}
              <div style={{ marginTop: 12 }}>
                <div style={{ height: 6, borderRadius: 99, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 99, width: `${prob}%`, background: probColor(prob), transition: 'width 0.3s' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Priority */}
          {opp.priority && (
            <div className="card">
              <div className="card-header"><span className="card-title">Priority</span></div>
              <div className="card-body">
                <span style={{
                  display: 'inline-block', padding: '4px 14px', borderRadius: 99, fontWeight: 700, fontSize: 13,
                  background: opp.priority === 'High' ? '#fee2e2' : opp.priority === 'Medium' ? '#fef3c7' : '#f0fdf4',
                  color: opp.priority === 'High' ? '#b91c1c' : opp.priority === 'Medium' ? '#a16207' : '#15803d',
                }}>
                  {opp.priority}
                </span>
              </div>
            </div>
          )}

          <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => navigate('/opportunity/list')}>
            ← Back to Opportunities
          </button>
        </div>
      </div>

      <ConfirmModal
        open={showDelete}
        title="Delete Opportunity"
        message="Are you sure you want to permanently delete this opportunity?"
        confirmLabel="Delete"
        confirmDanger
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

export default SingleOpportunityDetails;
